import React, { useState, useEffect, useRef } from 'react';
import { getAllSavedAudio, deleteSavedAudio } from '../utils/indexedDB'; // Adjust path as needed
import { FaPlay, FaPause, FaTrash, FaArrowLeft } from 'react-icons/fa'; // Import FaArrowLeft
import { useNavigate } from 'react-router-dom'; // Import useNavigate for navigation

const OfflinePage = () => {
    const [savedAudios, setSavedAudios] = useState([]);
    const [currentPlayingId, setCurrentPlayingId] = useState(null);
    const [currentPlayingName, setCurrentPlayingName] = useState('');
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef(new Audio());

    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    // New state to track online status within this component
    const [isComponentOnline, setIsComponentOnline] = useState(navigator.onLine);
    const navigate = useNavigate(); // Initialize useNavigate hook

    // Effect to load saved audios from IndexedDB
    useEffect(() => {
        const loadSavedAudios = async () => {
            const audios = await getAllSavedAudio();
            setSavedAudios(audios);
        };

        loadSavedAudios();

        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = '';
            }
        };
    }, []);

    // Effect to handle audio playback events and synchronize state
    useEffect(() => {
        const audio = audioRef.current;

        const handleEnded = () => {
            console.log("Аудио завершилось.");
            setIsPlaying(false);
            setCurrentPlayingId(null);
            setCurrentPlayingName('');
            setCurrentTime(0);
            setDuration(0);
            if (audio.src.startsWith('blob:')) {
                URL.revokeObjectURL(audio.src);
            }
            audio.src = '';
        };

        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
        const handleLoadedMetadata = () => setDuration(audio.duration);

        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);

        return () => {
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        };
    }, [currentPlayingId]);

    // Effect for network status detection (specific to this component's UI)
    useEffect(() => {
        const handleOnlineStatusChange = () => {
            const online = navigator.onLine;
            setIsComponentOnline(online);
            if (online) {
                console.log("Went online, stopping audio (from OfflinePage's internal check).");
                if (audioRef.current && !audioRef.current.paused) {
                    audioRef.current.pause();
                    setCurrentPlayingId(null);
                    setCurrentPlayingName('');
                    setIsPlaying(false);
                    setCurrentTime(0);
                    setDuration(0);
                    if (audioRef.current.src.startsWith('blob:')) {
                        URL.revokeObjectURL(audioRef.current.src);
                    }
                    audioRef.current.src = '';
                }
                // Optional: You can navigate to home or refresh if you want a stronger redirect
                // navigate('/');
            }
        };

        window.addEventListener('online', handleOnlineStatusChange);
        window.addEventListener('offline', handleOnlineStatusChange); // Listen for offline too to update state

        return () => {
            window.removeEventListener('online', handleOnlineStatusChange);
            window.removeEventListener('offline', handleOnlineStatusChange);
        };
    }, [navigate]); // Add navigate to dependency array

    const handlePlayAudio = (audioBlob, audioName, audioId) => {
        if (!audioBlob) {
            console.error("No audioBlob provided for playback!");
            alert("Аудио файл қолжетімсіз.");
            return;
        }

        console.log("Попытка воспроизвести аудио:", audioName, "Размер Blob:", audioBlob.size, "Тип Blob:", audioBlob.type);

        if (currentPlayingId === audioId) {
            if (audioRef.current.paused) {
                audioRef.current.play().catch(e => console.error("Ошибка возобновления аудио:", e));
            } else {
                audioRef.current.pause();
            }
        } else {
            if (audioRef.current.src.startsWith('blob:')) {
                URL.revokeObjectURL(audioRef.current.src);
            }

            const newBlobUrl = URL.createObjectURL(audioBlob);

            setCurrentPlayingId(audioId);
            setCurrentPlayingName(audioName);

            audioRef.current.src = newBlobUrl;
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(e => {
                console.error("Ошибка воспроизведения аудио из Blob URL:", e);
                alert("Аудионы ойнату мүмкін болмады. Қайталап көріңіз.");
            });
        }
    };

    const handleDeleteAudio = async (audioUrlToDelete) => {
        const result = await deleteSavedAudio(audioUrlToDelete);
        if (result.success) {
            setSavedAudios(prevAudios => prevAudios.filter(audio => audio.audioUrl !== audioUrlToDelete));

            if (currentPlayingId === audioUrlToDelete) {
                audioRef.current.pause();
                if (audioRef.current.src.startsWith('blob:')) {
                    URL.revokeObjectURL(audioRef.current.src);
                }
                audioRef.current.src = '';
                setCurrentPlayingId(null);
                setCurrentPlayingName('');
                setIsPlaying(false);
                setCurrentTime(0);
                setDuration(0);
            }
            alert("Аудио жойылды!");
        } else {
            alert(`Аудионы жоюда қате пайда болды: ${result.error}`);
        }
    };

    const handleSliderChange = (e) => {
        const newTime = parseFloat(e.target.value);
        audioRef.current.currentTime = newTime;
        setCurrentTime(newTime);
    };

    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    return (
        <div className="flex flex-col items-center min-h-screen bg-gray-100 px-4 text-center pb-8">
            {isComponentOnline ? (
                // Header when online
                <header className="w-full flex justify-start items-center p-4 bg-white shadow-md mb-4">
                    <button
                        onClick={() => navigate('/')} // Navigate back to home
                        className="flex items-center text-blue-600 hover:text-blue-800"
                        title="Басты бетке оралу" // Back to home
                    >
                        <FaArrowLeft className="mr-2" />
                        <span>Басты бет</span>
                    </button>
                </header>
            ) : (
                // Offline message when offline
                <>
                    <h1 className="text-2xl font-bold text-gray-800 mb-4">Сіз офлайн режимдесіз</h1>
                    <p className="text-gray-600 mb-2">Қосымшаны пайдалану үшін интернетке қосылыңыз.</p>
                    <p className="text-gray-500 text-sm mb-6">Интернет байланысы қалпына келген кезде, бет автоматты түрде жаңарады.</p>
                </>
            )}

            <h2 className="text-xl font-semibold text-gray-700 mb-4">Сақталған аудиолар</h2>
            {savedAudios.length > 0 ? (
                <ul className="w-full max-w-md bg-white rounded-lg shadow-md p-4 space-y-3">
                    {savedAudios.map((audio) => (
                        <li key={audio.audioUrl} className="flex flex-col p-3 border-b border-gray-200 last:border-b-0">
                            <div className="flex items-center justify-between w-full mb-2">
                                <span className="text-gray-800 font-medium truncate flex-1 text-left">{audio.audioName}</span>
                                <div className="flex items-center space-x-3 ml-4">
                                    <button
                                        onClick={() => handlePlayAudio(audio.audioBlob, audio.audioName, audio.audioUrl)}
                                        className="p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                                        title={currentPlayingId === audio.audioUrl && isPlaying ? "Пауза" : "Ойнату"}
                                    >
                                        {currentPlayingId === audio.audioUrl && isPlaying ? <FaPause /> : <FaPlay />}
                                    </button>
                                    <button
                                        onClick={() => handleDeleteAudio(audio.audioUrl)}
                                        className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                                        title="Жою"
                                    >
                                        <FaTrash />
                                    </button>
                                </div>
                            </div>
                            {currentPlayingId === audio.audioUrl && (
                                <div className="flex items-center w-full mt-2">
                                    <span className="text-sm text-gray-600 mr-2">{formatTime(currentTime)}</span>
                                    <input
                                        type="range"
                                        min="0"
                                        max={duration || 0}
                                        value={currentTime}
                                        onChange={handleSliderChange}
                                        className="flex-1 h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer accent-blue-500"
                                    />
                                    <span className="text-sm text-gray-600 ml-2">{formatTime(duration)}</span>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="text-gray-500">Әлі сақталған аудиолар жоқ.</p>
            )}
        </div>
    );
};

export default OfflinePage;